import React from "react";
import {Link} from "react-router-dom";
import {useTranslation} from "@unibuddy/intl";
import {Alert, Box, Button, Heading, Icon, Stack, Text, Tooltip, Badge} from "@unibuddy/patron";
import {IconExclamationCircle} from "@unibuddy/icons";
import styled from "styled-components";

import {ChatHeaderUI} from "shared/Chat/components/ChatHeaderUI/ChatHeaderUI";
import {rootURL} from "shared/Utils/WhereAmI";
import useAnalytics from "shared/AnalyticsNew/hooks/useAnalytics";
import useLocalStorage from "shared/functions/useLocalStorage";
import {MentorAvatar} from "shared/Mentor/components/MentorAvatar/MentorAvatar";
import ThemedLink from "shared/Navigation/components/ThemedLink/ThemedLink";
import {ChatGroupField, UniversityField} from "src/types/generated";

import {ChatBackButton} from "../ChatBackButton/ChatBackButton";
import {getMentorInboxInfoOne} from "../../utils/InboxInfo";
import s from "./ChatHeader.pcss";
import {IntlNamespaces} from "../../../../../translations/types";
import {
    INTL_BAD_VIBES,
    INTL_CHAT_RULES,
    INTL_CONTACT_SUPPORT,
    INTL_GIVING_TIPS,
    INTL_GOT_IT,
    INTL_HAPPY_CHATTING,
    INTL_OFFENSIVE_MESSAGES,
    INTL_ROMANTIC_COMMENTS,
    INTL_SHARING_EXPERIENCE,
    INTL_SHARING_SOCIAL_MEDIA,
    INTL_START_A_CHAT,
    INTL_TALKING_ABOUT_COURSE,
    INTL_THINGS_WE_DONT_LIKE,
    INTL_THINGS_WE_LIKE,
} from "../../../../../translations/codeOfConduct";
import {INTL_MENTOR_PROFILE} from "../../../../../translations/chat";
import {isFunction} from "../../../../../util/isFunction";

const StyledBox = styled(Box)`
    display: ruby;
`;

const CodeOfConductText = ({t}) => (
    <Stack space="medium">
        <Text>
            {t(
                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_START_A_CHAT.key}`,
                INTL_START_A_CHAT.defaultValue,
            )}
        </Text>
        <Stack space="small">
            <Heading level="2" size="xxsmall">
                ✅{" "}
                {t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_THINGS_WE_LIKE.key}`,
                    INTL_THINGS_WE_LIKE.defaultValue,
                )}
            </Heading>
            <ul>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_SHARING_EXPERIENCE.key}`,
                                INTL_SHARING_EXPERIENCE.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_TALKING_ABOUT_COURSE.key}`,
                                INTL_TALKING_ABOUT_COURSE.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_GIVING_TIPS.key}`,
                                INTL_GIVING_TIPS.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
            </ul>
            <Heading level="2" size="xxsmall">
                ⛔{" "}
                {t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_THINGS_WE_DONT_LIKE.key}`,
                    INTL_THINGS_WE_DONT_LIKE.defaultValue,
                )}
            </Heading>
            <ul>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_OFFENSIVE_MESSAGES.key}`,
                                INTL_OFFENSIVE_MESSAGES.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_SHARING_SOCIAL_MEDIA.key}`,
                                INTL_SHARING_SOCIAL_MEDIA.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_BAD_VIBES.key}`,
                                INTL_BAD_VIBES.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
                <li>
                    <StyledBox>
                        <Text>
                            {t(
                                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_ROMANTIC_COMMENTS.key}`,
                                INTL_ROMANTIC_COMMENTS.defaultValue,
                            )}
                        </Text>
                    </StyledBox>
                </li>
            </ul>
            <Text>
                {t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_CONTACT_SUPPORT.key}`,
                    INTL_CONTACT_SUPPORT.defaultValue,
                )}
                <ThemedLink
                    external
                    to="mailto:support@unibuddy.com"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    support@unibuddy.com
                </ThemedLink>
            </Text>
        </Stack>
        <Text>
            {t(
                `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_HAPPY_CHATTING.key}`,
                INTL_HAPPY_CHATTING.defaultValue,
            )}
        </Text>
    </Stack>
);

type ChatHeaderProps = {
    chatRulesOverride: boolean;
    chatGroup: ChatGroupField;
    university: UniversityField;
};

export function LegacyChatHeader({chatRulesOverride, chatGroup, university}: ChatHeaderProps) {
    const {t} = useTranslation([IntlNamespaces.CHAT, IntlNamespaces.CODE_OF_CONDUCT]);
    const {
        events: {itemClicked},
        itemNames: {chatRules},
    } = useAnalytics();
    const [rulesOpen, setRulesOpen] = React.useState(false);
    const [chatRulesSeen, setChatRulesSeen] = useLocalStorage("chatRulesSeen", chatRulesOverride);
    const toggleRulesOpen = () => {
        if (!rulesOpen) {
            itemClicked({
                prefix: chatRules,
            });
            setChatRulesSeen(true);
        }
        setRulesOpen(!rulesOpen);
    };
    const rootUrl: string = rootURL(location.pathname);
    const opponentId: string = chatGroup.opponent.id;
    const buddyRole = `${chatGroup.opponent.accountRole === "staff" ? "staff" : "students"}`;
    const getBuddyPosition = () => (location.search ? location.search : "?buddyPosition=chat");
    const mentorLink = `${rootUrl}/buddies/${buddyRole}/${opponentId}${getBuddyPosition()}`;

    const mentorsName: string = chatGroup.opponent.firstName;
    const inboxInfoOne =
        getMentorInboxInfoOne({sender: chatGroup.opponent, university, t}) ||
        chatGroup.opponent.inboxInfoOne;

    return (
        <>
            <ChatHeaderUI
                leftAddon={<ChatBackButton />}
                rightAddon={
                    <Button
                        size="sm"
                        round
                        iconOnly
                        aria-label={t(
                            `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_CHAT_RULES.key}`,
                            INTL_CHAT_RULES.defaultValue,
                        )}
                        onClick={toggleRulesOpen}
                    >
                        <Icon icon={IconExclamationCircle} color={chatRulesSeen ? "grey" : "red"} />
                    </Button>
                }
            >
                {chatGroup.opponent && (
                    <React.Fragment>
                        <ChatHeaderUI.Title>
                            <div>
                                <Link
                                    className={s.mentorProfileLink}
                                    to={mentorLink}
                                    aria-label={t(
                                        `${IntlNamespaces.CHAT}:${INTL_MENTOR_PROFILE.key}`,
                                        isFunction(INTL_MENTOR_PROFILE.defaultValue)
                                            ? INTL_MENTOR_PROFILE.defaultValue(mentorsName)
                                            : INTL_MENTOR_PROFILE.defaultValue,
                                        {
                                            name: mentorsName,
                                        },
                                    )}
                                >
                                    <MentorAvatar
                                        data-test-id="chat-header-mentor-avatar"
                                        mentor={chatGroup.opponent}
                                    />
                                    <div className={s.mentorInfo}>
                                        {mentorsName} <br />
                                        {inboxInfoOne && inboxInfoOne.length > 48 ? (
                                            <Tooltip label={inboxInfoOne}>
                                                <small className={s.cursorDefault}>
                                                    {inboxInfoOne}
                                                </small>
                                            </Tooltip>
                                        ) : (
                                            <small className={s.cursorDefault}>
                                                {inboxInfoOne}
                                            </small>
                                        )}
                                        <br />
                                        {chatGroup.opponent.isEnterprise &&
                                            (chatGroup.opponent.inboxInfoTwo &&
                                            chatGroup.opponent.inboxInfoTwo.length > 48 ? (
                                                <div>
                                                    <Tooltip
                                                        label={chatGroup.opponent.inboxInfoTwo}
                                                    >
                                                        <small className={s.cursorDefault}>
                                                            <strong>
                                                                {chatGroup.opponent.inboxInfoTwo}
                                                            </strong>
                                                        </small>
                                                    </Tooltip>
                                                </div>
                                            ) : (
                                                <small className={s.cursorDefault}>
                                                    <strong>
                                                        {chatGroup.opponent.inboxInfoTwo}
                                                    </strong>
                                                </small>
                                            ))}
                                    </div>
                                </Link>
                            </div>
                        </ChatHeaderUI.Title>
                        {chatGroup.creationSource &&
                            chatGroup.creationSource.type === "marketplace" && (
                                <Badge
                                    className={s.sourceBadge}
                                    color="violet"
                                    size="standard"
                                    elevation={0}
                                >
                                    {chatGroup.creationSource.name}
                                </Badge>
                            )}
                    </React.Fragment>
                )}
            </ChatHeaderUI>
            <Alert
                title={t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_CHAT_RULES.key}`,
                    INTL_CHAT_RULES.defaultValue,
                )}
                onDismiss={toggleRulesOpen}
                isOpen={rulesOpen}
                size="xsmall"
                dismissText={t(
                    `${IntlNamespaces.CODE_OF_CONDUCT}:${INTL_GOT_IT.key}`,
                    INTL_GOT_IT.defaultValue,
                )}
                type="light"
                titleClassname={s.codeOfConductTitle}
                buttonsClassname={s.codeOfConductButtons}
            >
                <CodeOfConductText t={t} />
            </Alert>
        </>
    );
}
